import { Dialog, DialogContent } from "@/components/ui/dialog";
import { X } from "lucide-react";

interface VideoModalProps {
  isOpen: boolean;
  onClose: () => void;
  videoUrl: string;
  title?: string;
  format?: 'desktop' | 'mobile'; // desktop = 16:9, mobile = 9:16
}

const VideoModal = ({ isOpen, onClose, videoUrl, title = "Dine AI Demo", format = 'desktop' }: VideoModalProps) => {
  // Convert YouTube URL to embed format with strict domain validation
  const getEmbedUrl = (url: string): string => {
    try {
      // Handle YouTube Shorts URLs
      if (url.includes('youtube.com/shorts/')) {
        const parts = url.split('shorts/');
        if (parts.length > 1) {
          const videoId = parts[1].split('?')[0];
          if (videoId) {
            return `https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`;
          }
        }
      }
      // Handle regular YouTube URLs
      if (url.includes('youtube.com/watch?v=')) {
        const parts = url.split('v=');
        if (parts.length > 1) {
          const videoId = parts[1].split('&')[0];
          if (videoId) {
            return `https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`;
          }
        }
      }
      // Handle youtu.be URLs
      if (url.includes('youtu.be/')) {
        const parts = url.split('youtu.be/');
        if (parts.length > 1) {
          const videoId = parts[1].split('?')[0];
          if (videoId) {
            return `https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`;
          }
        }
      }

      // If URL is already a YouTube embed URL, validate and return it
      if (url.startsWith('https://www.youtube.com/embed/')) {
        return url;
      }
    } catch (error) {
      console.error('Error parsing YouTube URL:', error);
    }

    // SECURITY: Only allow YouTube embed URLs - return empty string if validation fails
    console.warn('Invalid YouTube URL provided to VideoModal:', url);
    return '';
  };

  // Aspect ratio calculations
  // Desktop (16:9): paddingBottom = 9/16 * 100 = 56.25%
  // Mobile (9:16): paddingBottom = 16/9 * 100 = 177.78%
  const aspectRatio = format === 'mobile' ? '177.78%' : '56.25%';
  const maxWidth = format === 'mobile' ? 'max-w-md' : 'max-w-5xl';

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className={`${maxWidth} w-full p-0 bg-black border-none overflow-hidden`}>
        {/* Header with close button */}
        <div className="absolute top-4 right-4 z-50">
          <button
            onClick={onClose}
            className="bg-black/50 hover:bg-black/70 text-white rounded-full p-2 transition-all"
            aria-label="Close video"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Video Container - Dynamic aspect ratio */}
        <div className="relative w-full" style={{ paddingBottom: aspectRatio }}>
          <iframe
            className="absolute top-0 left-0 w-full h-full"
            src={getEmbedUrl(videoUrl)}
            title={title}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            style={{ border: 'none' }}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default VideoModal;
